import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraDataTable } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEntityCell, SpecraInlineBanner, SpecraSectionHeader, SpecraSegmentedControl, SpecraTooltip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { registryHref } from '../../registryHref';
export function RateValue({ cell, inferred = false, currency = 'USD', lowest = false }) {
    if (!cell) return '—';
    const amount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
    }).format(cell.amount);
    return /*#__PURE__*/ _jsx(SpecraEntityCell, {
        title: `${amount}${inferred ? ' est.' : ''}`,
        description: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                cell.source ? /*#__PURE__*/ _jsx(SpecraLink, {
                    href: registryHref(cell.source) ?? undefined,
                    renderLink: renderRouterLink,
                    children: cell.sourceLabel || 'Source not recorded'
                }) : cell.sourceLabel || 'Source not recorded',
                lowest && cell.observationCount > 1 && ` · lowest of ${cell.observationCount}`
            ]
        })
    });
}
export function RateGap({ row }) {
    const gap = row.gap;
    const label = gap?.kind === 'PERCENT' ? `${gap.percent < 0 ? '−' : '+'}${Math.abs(gap.percent)}%` : gap?.kind === 'CEILING_ONLY' ? 'ceiling only' : gap?.kind === 'INFERRED_ONLY' ? 'inferred' : gap?.kind === 'NOT_COMPARABLE' ? 'not comparable' : '—';
    const badge = /*#__PURE__*/ _jsx(SpecraBadge, {
        className: "whitespace-normal",
        "data-testid": Testids.registryCompany.gapPill,
        variant: gap?.kind === 'PERCENT' ? 'success' : gap?.kind === 'INFERRED_ONLY' ? 'warning' : 'neutral',
        children: label
    });
    return gap?.notComparableReasons.length ? /*#__PURE__*/ _jsx(SpecraTooltip, {
        content: gap.notComparableReasons.join(' · '),
        children: /*#__PURE__*/ _jsx(SpecraButton, {
            variant: "ghost",
            size: "xs",
            className: "h-auto whitespace-normal",
            "aria-label": `Gap: ${gap.notComparableReasons.join(' · ')}`,
            children: badge
        })
    }) : badge;
}
// specra-allow local-composite: REG-15 domain columns, vehicle filter and rule over SpecraDataTable, no restyle
export function CompanyRateBookTable({ book, vehicleId, onVehicleChange }) {
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Rate book · company level",
                action: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "Every number keeps its type and source. Ceilings never silently become the price."
                })
            }),
            /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "Vehicle",
                size: "sm",
                testId: Testids.registryCompany.rateBookVehicle,
                value: vehicleId,
                onValueChange: onVehicleChange,
                options: [
                    {
                        value: '',
                        label: 'All vehicles'
                    },
                    ...book.vehicles.map((v)=>({
                            value: v.id,
                            label: v.name
                        }))
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                rows: book.rows,
                getRowId: (r)=>r.rowKey,
                rowTestId: Testids.registryCompany.rateBookRow,
                emptyTitle: "No rates recorded for this company yet.",
                columns: [
                    {
                        id: 'category',
                        header: 'Their labor category (as written)',
                        cell: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                                title: /*#__PURE__*/ _jsx(SpecraLink, {
                                    href: `/registries/labor-categories/entries/${encodeURIComponent(r.lcatEntryId)}`,
                                    renderLink: renderRouterLink,
                                    children: r.titleAsWritten
                                }),
                                description: r.vehicle?.name
                            })
                    },
                    {
                        id: 'mapping',
                        header: 'Maps to',
                        cell: (r)=>r.mapping?.anchorId ? /*#__PURE__*/ _jsx(SpecraEntityCell, {
                                title: /*#__PURE__*/ _jsx(SpecraLink, {
                                    href: registryHref({
                                        kind: 'LCAT_ANCHOR',
                                        id: r.mapping.anchorId
                                    }) ?? undefined,
                                    renderLink: renderRouterLink,
                                    children: r.mapping.anchorTitle
                                }),
                                description: `${r.mapping.status === 'APPROVED' ? 'reviewed' : 'candidate · needs review'}${r.mapping.socCode ? ` · SOC ${r.mapping.socCode}` : ''}`
                            }) : 'Not mapped'
                    },
                    {
                        id: 'period',
                        header: 'Period',
                        cell: (r)=>r.periodDisplay || 'Period not stated'
                    },
                    {
                        id: 'ceiling',
                        header: 'Ceiling',
                        align: 'right',
                        cell: (r)=>/*#__PURE__*/ _jsx(RateValue, {
                                cell: r.ceiling,
                                currency: r.currency
                            })
                    },
                    {
                        id: 'agreed',
                        header: 'Agreed',
                        align: 'right',
                        cell: (r)=>/*#__PURE__*/ _jsx(RateValue, {
                                cell: r.agreed,
                                currency: r.currency,
                                lowest: true
                            })
                    },
                    {
                        id: 'billed',
                        header: 'Billed',
                        align: 'right',
                        cell: (r)=>/*#__PURE__*/ _jsx(RateValue, {
                                cell: r.billed ?? r.inferred,
                                inferred: !r.billed && !!r.inferred,
                                currency: r.currency,
                                lowest: true
                            })
                    },
                    {
                        id: 'gap',
                        header: 'Gap',
                        cell: (r)=>/*#__PURE__*/ _jsx(RateGap, {
                                row: r
                            })
                    }
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                variant: "framed",
                tone: "neutral",
                "data-testid": Testids.registryCompany.ruleBanner,
                children: [
                    book.suggestionRuleLabel || 'Pricing suggestions keep ceilings separate from real rates and label every suggestion.',
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        tone: "muted",
                        children: "Gaps appear once a sheet's burden basis is stated (review queue) and agreed rates are entered with their period."
                    })
                ]
            })
        ]
    });
}
